import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Coordinates the once-per-session intro animation (owned by AppComponent)
 * with other components that need to react to it (e.g. HomeComponent, which
 * holds its awards animation until the intro is finished).
 *
 * The `introAnimation` sessionStorage flag remains the source of truth for
 * "has the intro already played this session"; this service simply broadcasts
 * completion so components can respond without polling storage.
 */
@Injectable({ providedIn: 'root' })
export class IntroService {
  private static readonly STORAGE_KEY = 'introAnimation';

  private readonly _introDone$ = new BehaviorSubject<boolean>(this.readInitialState());
  readonly introDone$: Observable<boolean> = this._introDone$.asObservable();

  /** Whether the intro has already completed (this session). */
  get isDone(): boolean {
    return this._introDone$.value;
  }

  /** Marks the intro as complete, persisting the flag and notifying subscribers. */
  complete(): void {
    try {
      sessionStorage.setItem(IntroService.STORAGE_KEY, 'true');
    } catch {
      // sessionStorage may be unavailable (e.g. private mode); still notify.
    }
    if (!this._introDone$.value) {
      this._introDone$.next(true);
    }
  }

  private readInitialState(): boolean {
    try {
      return !!sessionStorage.getItem(IntroService.STORAGE_KEY);
    } catch {
      return false;
    }
  }
}
